import type { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import {
  InventoryPermissions,
  OrderManagementPermissions,
} from "@redotech/redo-model/permissions";

export const warehouseRoutes: PermissionedRouteObject[] = [
  {
    async lazy() {
      const [
        { WmsProvider },
        { SuppressPrintNotificationsProvider },
        { TaskPlatformProviders },
        { Outlet },
      ] = await Promise.all([
        import("@redotech/redo-merchant-fulfillment-app/common/wms-context"),
        import(
          "@redotech/redo-merchant-fulfillment-app/common/print-queue-context"
        ),
        import(
          "@redotech/redo-merchant-fulfillment-app/tasks/task-platform-providers"
        ),
        import("react-router-dom"),
      ]);

      return {
        element: (
          <WmsProvider>
            <TaskPlatformProviders>
              <SuppressPrintNotificationsProvider>
                <Outlet />
              </SuppressPrintNotificationsProvider>
            </TaskPlatformProviders>
          </WmsProvider>
        ),
      };
    },
    children: [
      {
        index: true,
        handle: { breadcrumb: "Warehouse Management" },
        async lazy() {
          const { WarehouseLandingPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/warehouse-landing-page"
          );
          return { Component: WarehouseLandingPage };
        },
      },
      {
        path: "transfer",
        handle: { breadcrumb: "Transfer" },
        async lazy() {
          const [{ TransferProvider }, { Outlet }] = await Promise.all([
            import(
              "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-context"
            ),
            import("react-router-dom"),
          ]);
          return {
            element: (
              <TransferProvider>
                <Outlet />
              </TransferProvider>
            ),
          };
        },
        children: [
          {
            index: true,
            async lazy() {
              const { TransferPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-page"
              );
              return { Component: TransferPage };
            },
          },
          {
            path: "items",
            handle: { breadcrumb: "Select Items" },
            async lazy() {
              const { TransferItemsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-items-page"
              );
              return { Component: TransferItemsPage };
            },
          },
          {
            path: "lost-items",
            handle: { breadcrumb: "Find Items" },
            async lazy() {
              const { TransferLostItemsPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-lost-items-page"
              );
              return { Component: TransferLostItemsPage };
            },
          },
          {
            path: "route",
            handle: { breadcrumb: "Transfer Route" },
            async lazy() {
              const { TransferRoutePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/transfer/transfer-route-page"
              );
              return { Component: TransferRoutePage };
            },
          },
        ],
      },
      {
        path: "instant-transfer",
        handle: { breadcrumb: "Instant Transfer" },
        async lazy() {
          const { InstantTransferPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/instant-transfer/instant-transfer-page"
          );
          return { Component: InstantTransferPage };
        },
      },
      {
        path: "count",
        handle: { breadcrumb: "Count" },
        children: [
          {
            index: true,
            async lazy() {
              const { CountPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/bin-count/count-page"
              );
              return { Component: CountPage };
            },
          },
          {
            path: "session",
            handle: { breadcrumb: "Count Bins" },
            async lazy() {
              const { BinCountSessionPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/bin-count/bin-count-session-page"
              );
              return { Component: BinCountSessionPage };
            },
          },
          {
            path: "sku",
            handle: { breadcrumb: "Count by SKU" },
            async lazy() {
              const { ImsSkuCountPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/bin-count/ims-sku-count-page"
              );
              return { Component: ImsSkuCountPage };
            },
          },
        ],
      },
      {
        path: "receive",
        handle: { breadcrumb: "Receive" },
        async lazy() {
          const { ReceivingPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/receiving/receiving-page"
          );
          return { Component: ReceivingPage };
        },
      },
      {
        path: "receive-shipment",
        handle: { breadcrumb: "Receive Shipment" },
        async lazy() {
          const { ReceiveShipmentSearchPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/receiving/receive-shipment-search-page"
          );
          return { Component: ReceiveShipmentSearchPage };
        },
      },
      {
        path: "receive-shipment/:shipmentId",
        handle: { breadcrumb: "Receive Items" },
        async lazy() {
          const { ReceiveShipmentPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/receiving/receive-shipment-page"
          );
          return { Component: ReceiveShipmentPage };
        },
      },
      {
        path: "replenishment",
        handle: { breadcrumb: "Replenishment" },
        async lazy() {
          const [{ ReplenishmentProvider }, { Outlet }] = await Promise.all([
            import(
              "@redotech/redo-merchant-fulfillment-app/wms/replenishment/replenishment-context"
            ),
            import("react-router-dom"),
          ]);
          return {
            element: (
              <ReplenishmentProvider>
                <Outlet />
              </ReplenishmentProvider>
            ),
          };
        },
        children: [
          {
            index: true,
            async lazy() {
              const { ReplenishmentDashboardPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/replenishment/replenishment-dashboard-page"
              );
              return { Component: ReplenishmentDashboardPage };
            },
          },
          {
            path: "source",
            handle: { breadcrumb: "Source Items" },
            async lazy() {
              const { ReplenishmentSourcePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/replenishment/replenishment-source-page"
              );
              return { Component: ReplenishmentSourcePage };
            },
          },
          {
            path: "distribute",
            handle: { breadcrumb: "Distribute Items" },
            async lazy() {
              const { ReplenishmentDistributePage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/replenishment/replenishment-distribute-page"
              );
              return { Component: ReplenishmentDistributePage };
            },
          },
        ],
      },
      {
        path: "bin-assignment",
        handle: { breadcrumb: "Bin Assignment" },
        children: [
          {
            index: true,
            async lazy() {
              const { BinAssignmentPage } = await import(
                "@redotech/redo-merchant-fulfillment-app/wms/bin-assignment/bin-assignment-page"
              );
              return { Component: BinAssignmentPage };
            },
          },
        ],
      },
      {
        path: "work-order-station",
        handle: { breadcrumb: "Work Order Station" },
        async lazy() {
          const { WorkOrderStationPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/work-order-station/work-order-station-page"
          );
          return { Component: WorkOrderStationPage };
        },
      },
      {
        path: "consolidation",
        handle: { breadcrumb: "Consolidation" },
        async lazy() {
          const { ConsolidationPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidation-page"
          );
          return { Component: ConsolidationPage };
        },
      },
      {
        path: "processing",
        handle: {
          breadcrumb: "Returns Processing",
          permission: [
            OrderManagementPermissions.VIEW_RETURN,
            OrderManagementPermissions.EDIT_RETURN,
            InventoryPermissions.ACCESS_INVENTORY,
          ],
        },
        children: [
          {
            async lazy() {
              const { ReturnsProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
              );
              return { Component: ReturnsProcessingProviders };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ReturnLookupPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/lookup-page"
                  );
                  return { Component: ReturnLookupPage };
                },
              },
              {
                path: ":returnLabel",
                async lazy() {
                  const { ProcessingProviders } = await import(
                    "@redotech/redo-merchant-returns-processing-app/processing-providers"
                  );
                  return { Component: ProcessingProviders };
                },
                children: [
                  {
                    index: true,
                    handle: { breadcrumb: "Process Return" },
                    async lazy() {
                      const { ProcessingRouter } = await import(
                        "@redotech/redo-merchant-returns-processing-app/processing-router"
                      );
                      return { Component: ProcessingRouter };
                    },
                  },
                  // Routes for configurable flow (when feature flag is enabled)
                  {
                    path: "flow",
                    handle: { breadcrumb: "Return Flow" },
                    async lazy() {
                      const { FlowStepPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/flow-step-page"
                      );
                      return { Component: FlowStepPage };
                    },
                  },
                  {
                    path: "flow/:sessionItemId",
                    handle: { breadcrumb: "Flow Step" },
                    async lazy() {
                      const { FlowStepPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/flow-step-page"
                      );
                      return { Component: FlowStepPage };
                    },
                  },
                  // Routes for hardcoded flow (when feature flag is disabled)
                  {
                    path: "inspect/:sessionItemId",
                    handle: { breadcrumb: "Inspect Item" },
                    async lazy() {
                      const { ItemInspectionPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/inspection-page"
                      );
                      return { Component: ItemInspectionPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "processing-flow",
        handle: {
          breadcrumb: "Returns Processing Flow",
          permission: [
            OrderManagementPermissions.VIEW_RETURN,
            OrderManagementPermissions.EDIT_RETURN,
            InventoryPermissions.ACCESS_INVENTORY,
          ],
        },
        children: [
          {
            async lazy() {
              const { ReturnsProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
              );
              return { Component: ReturnsProcessingProviders };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ReturnLookupPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/lookup-page"
                  );
                  return { Component: ReturnLookupPage };
                },
              },
              {
                path: ":returnLabel",
                async lazy() {
                  const { ProcessingProviders } = await import(
                    "@redotech/redo-merchant-returns-processing-app/processing-providers"
                  );
                  return { Component: ProcessingProviders };
                },
                children: [
                  {
                    index: true,
                    handle: { breadcrumb: "Process Return" },
                    async lazy() {
                      const { ReturnProcessingFlowPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/processing-page-flow"
                      );
                      return { Component: ReturnProcessingFlowPage };
                    },
                  },
                  {
                    path: "flow",
                    handle: { breadcrumb: "Return Flow" },
                    async lazy() {
                      const { FlowStepPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/flow-step-page"
                      );
                      return { Component: FlowStepPage };
                    },
                  },
                  {
                    path: "flow/:sessionItemId",
                    handle: { breadcrumb: "Flow Step" },
                    async lazy() {
                      const { FlowStepPage } = await import(
                        "@redotech/redo-merchant-returns-processing-app/flow-step-page"
                      );
                      return { Component: FlowStepPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "sorting",
        handle: {
          breadcrumb: "Returns Sorting",
          permission: [
            OrderManagementPermissions.VIEW_RETURN,
            OrderManagementPermissions.EDIT_RETURN,
            InventoryPermissions.ACCESS_INVENTORY,
          ],
        },
        children: [
          {
            async lazy() {
              const { ReturnsProcessingProviders } = await import(
                "@redotech/redo-merchant-returns-processing-app/returns-processing-providers"
              );
              return { Component: ReturnsProcessingProviders };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ReturnSortingPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/sorting-page"
                  );
                  return { Component: ReturnSortingPage };
                },
              },
              {
                path: "quarantine",
                handle: { breadcrumb: "Quarantine Item" },
                async lazy() {
                  const { QuarantinePage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/quarantine-page"
                  );
                  return { Component: QuarantinePage };
                },
              },
              {
                path: ":returnId",
                handle: { breadcrumb: "Sort Return" },
                async lazy() {
                  const { SortingDetailPage } = await import(
                    "@redotech/redo-merchant-returns-processing-app/sorting-detail-page"
                  );
                  return { Component: SortingDetailPage };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
